import React from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { aboutImage, regions, stats } from '../mock/data';
import { useI18n } from '../context/I18nContext';

const About = () => {
  const { t } = useI18n();
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    { t: t('why.f1t'), d: t('why.f1d') },
    { t: t('why.f2t'), d: t('why.f2d') },
    { t: t('why.f3t'), d: t('why.f3d') },
    { t: t('why.f4t'), d: t('why.f4d') }
  ];

  return (
    <>
      <section id="about" className="relative py-24 lg:py-32 bg-[#0b2545] overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(${aboutImage})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b2545] via-[#0b2545]/95 to-[#0b2545]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs tracking-[0.3em] uppercase text-[#f7a11a] font-semibold">{t('about.eyebrow')}</span>
              <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white leading-[1.05] tracking-tight">{t('about.title')}</h2>
              <Button onClick={() => scrollTo('#quote')} variant="outline" className="mt-8 bg-transparent border-2 border-[#f7a11a] text-[#f7a11a] hover:bg-[#f7a11a] hover:text-slate-900 h-12 px-6 rounded-none font-semibold tracking-wide text-sm">
                {t('cta.learnMore')} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            <div className="lg:col-span-7">
              <p className="text-slate-200 text-lg leading-relaxed">{t('about.p1')}</p>
              <p className="mt-5 text-slate-300 leading-relaxed">{t('about.p2')}</p>
              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="text-sm text-white/60 tracking-widest uppercase mb-4">{t('about.regionsLabel')}</div>
                <div className="flex flex-wrap gap-2">
                  {regions.map((r) => (
                    <span key={r} className="px-4 py-2 bg-white/5 border border-white/10 text-slate-200 text-sm hover:bg-[#f7a11a]/10 hover:border-[#f7a11a]/40 transition-colors">{r}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {stats.map((s, i) => (
              <div key={i} className="bg-[#0b2545] p-8 group hover:bg-[#0b2545]/60 transition-colors">
                <div className="text-5xl lg:text-6xl font-black text-[#f7a11a] tracking-tight">{s.number}</div>
                <div className="mt-2 text-white/80 text-sm tracking-wide">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="why" className="py-24 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="text-xs tracking-[0.3em] uppercase text-[#f7a11a] font-semibold">{t('why.eyebrow')}</span>
              <h2 className="mt-4 text-4xl lg:text-5xl font-black text-slate-900 leading-tight tracking-tight">{t('why.title')}</h2>
              <p className="mt-6 text-slate-600 leading-relaxed">{t('why.desc')}</p>
            </div>

            <div className="lg:col-span-7 space-y-4">
              {features.map((f, i) => (
                <div key={i} className="group bg-white border border-slate-200 p-6 lg:p-8 flex gap-5 hover:border-[#f7a11a] hover:shadow-lg transition-all">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#0b2545] group-hover:bg-[#f7a11a] flex items-center justify-center transition-colors">
                    <CheckCircle2 className="w-6 h-6 text-white group-hover:text-slate-900" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">{f.t}</h3>
                    <p className="mt-2 text-slate-600 leading-relaxed">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
